import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format, parseISO } from 'date-fns';
import { dropTask } from 'ember-concurrency';
import { not } from 'ember-truth-helpers';
import ProfilePhoto from 'garaje/components/employees/profile-photo';
import type AjaxService from 'garaje/services/ajax';
import type CurrentAdminService from 'garaje/services/current-admin';
import urlBuilder from 'garaje/utils/url-builder';

interface MoveRequestProfileResponse {
  employeeName: string;
  department: string | null;
  assignedDesks: AssignedDeskInfo[];
  assignedParking: AssignedParkingInfo | null;
  moveRequestStatuses: MoveRequestStatus[];
  canRequest: boolean;
}

interface AssignedDeskInfo {
  deskId: number;
  clientId: string;
  name: string;
  floorId: number;
  floorName: string | null;
  locationId: number;
  locationName: string | null;
  neighborhoodId: number | null;
  neighborhoodName: string | null;
}

interface AssignedParkingInfo {
  resourceId: number;
  name: string;
  locationName: string | null;
}

enum StatusType {
  PENDING_REQUEST = 'PENDING_REQUEST',
  UPCOMING_CHANGE = 'UPCOMING_CHANGE',
}

enum ChangeType {
  ASSIGNMENT = 'ASSIGNMENT',
  CHANGE = 'CHANGE',
  UNASSIGNMENT = 'UNASSIGNMENT',
}

interface MoveRequestStatus {
  moveRequestId: string;
  statusType: StatusType;
  changeType: ChangeType;
  assignmentId: number;
  newDeskName: string | null;
  newFloorName: string | null;
  locationId: number;
  locationName: string | null;
  date: string | null;
}

interface DisplayStatus {
  moveRequestId: string;
  assignmentId: number;
  title: string;
  newDeskLine: string | null;
  dateLine: string | null;
  showCancelLink: boolean;
}

interface DisplayDesk {
  deskId: number;
  displayText: string;
  neighborhoodName: string | null;
}

interface ProfileCardSignature {
  Args: {
    onClose: () => void;
  };
  Element: HTMLDivElement;
}

export default class MapsProfileCardComponent extends Component<ProfileCardSignature> {
  @service declare ajax: AjaxService;
  @service declare currentAdmin: CurrentAdminService;

  @tracked profileData: MoveRequestProfileResponse | null = null;
  @tracked hasError = false;

  constructor(owner: unknown, args: ProfileCardSignature['Args']) {
    super(owner, args);
    void this.loadProfile.perform();
  }

  get userPhotoUrl(): string | undefined {
    return this.currentAdmin.user?.userPhotoUrl;
  }

  get hasDesks(): boolean {
    return (this.profileData?.assignedDesks?.length ?? 0) > 0;
  }

  get desks(): DisplayDesk[] {
    return (this.profileData?.assignedDesks ?? []).map((desk) => ({
      deskId: desk.deskId,
      displayText: [desk.name, desk.floorName, desk.locationName].filter(Boolean).join(', '),
      neighborhoodName: desk.neighborhoodName,
    }));
  }

  get parkingText(): string | null {
    const parking = this.profileData?.assignedParking;
    if (!parking) return null;
    return [parking.name, parking.locationName].filter(Boolean).join(', ');
  }

  get upcomingStatuses(): DisplayStatus[] {
    return (this.profileData?.moveRequestStatuses ?? [])
      .filter((s) => s.statusType === StatusType.UPCOMING_CHANGE)
      .map((s) => ({
        moveRequestId: s.moveRequestId,
        assignmentId: s.assignmentId,
        title: `You have an upcoming desk ${s.changeType.toLowerCase()}`,
        newDeskLine: s.newDeskName ? `New desk: ${s.newDeskName}${s.newFloorName ? `, ${s.newFloorName}` : ''}` : null,
        dateLine: s.date ? `Date: ${format(parseISO(s.date), 'MMM dd, yyyy')}` : null,
        showCancelLink: false,
      }));
  }

  get pendingStatuses(): DisplayStatus[] {
    return (this.profileData?.moveRequestStatuses ?? [])
      .filter((s) => s.statusType === StatusType.PENDING_REQUEST)
      .map((s) => ({
        moveRequestId: s.moveRequestId,
        assignmentId: s.assignmentId,
        title: `Your desk ${s.changeType.toLowerCase()} request is pending`,
        newDeskLine: s.newDeskName ? `New desk: ${s.newDeskName}${s.newFloorName ? `, ${s.newFloorName}` : ''}` : null,
        dateLine: null,
        showCancelLink: true,
      }));
  }

  get buttonText(): string {
    return this.hasDesks ? 'Request desk change' : 'Request desk assignment';
  }

  get canRequest(): boolean {
    return this.profileData?.canRequest ?? false;
  }

  loadProfile = dropTask(async () => {
    try {
      this.hasError = false;

      const employeeId = this.currentAdmin.employee?.id;
      if (!employeeId) {
        this.hasError = true;
        return;
      }

      this.profileData = await this.ajax.request<MoveRequestProfileResponse>(
        urlBuilder.moveRequests.profileUrl(employeeId),
      );
    } catch {
      this.hasError = true;
    }
  });

  <template>
    {{! template-lint-disable use-ui-card }}
    {{! Invisible overlay to close card when clicking outside }}
    <div class="fixed inset-0 z-[100]" {{on "click" @onClose}} data-test-profile-card-overlay></div>

    {{! Profile card popover }}
    <div
      class="absolute right-0 top-full z-[101] mt-2 w-72 rounded-lg border border-carbon-10 bg-white p-5 shadow-lg"
      data-test-profile-card
      ...attributes
    >
      {{#if this.loadProfile.isRunning}}
        <div class="flex justify-center py-8">
          <span class="text-sm text-carbon-40">Loading...</span>
        </div>
      {{else if this.hasError}}
        <div class="py-8 text-center">
          <p class="text-sm text-carbon-50">Unable to load profile</p>
        </div>
      {{else if this.profileData}}
        {{! Header: Avatar + Name + Department }}
        <div class="mb-5 flex items-center">
          <ProfilePhoto
            @id={{this.currentAdmin.user.id}}
            @name={{this.currentAdmin.user.fullName}}
            @devicePhoto={{this.userPhotoUrl}}
            @isDisabled={{true}}
            @size={{2.5}}
            class="mr-3 shrink-0"
            data-test-profile-card-avatar
          />
          <div>
            <div class="text-xl font-bold text-carbon-70" data-test-profile-card-name>
              {{this.profileData.employeeName}}
            </div>
            {{#if this.profileData.department}}
              <div class="text-carbon-60" data-test-profile-card-department>
                {{this.profileData.department}}
              </div>
            {{/if}}
          </div>
        </div>

        {{! Assigned desk section }}
        <div class="mb-4">
          <div class="mb-1 font-bold text-carbon-50" data-test-profile-card-desk-label>Assigned desk</div>
          {{#if this.hasDesks}}
            {{#each this.desks as |desk|}}
              <div class="text-carbon-70" data-test-profile-card-desk-info>
                {{desk.displayText}}
              </div>
              {{#if desk.neighborhoodName}}
                <div class="text-gem-50" data-test-profile-card-neighborhood>
                  {{desk.neighborhoodName}}
                </div>
              {{/if}}
            {{/each}}
          {{else}}
            <div class="text-carbon-70" data-test-profile-card-no-desk>No assigned desk</div>
          {{/if}}
        </div>

        {{! Upcoming change banners (rendered before pending) }}
        {{#each this.upcomingStatuses as |status|}}
          <div class="mb-4 rounded-lg bg-gem-5 p-3" data-test-profile-card-upcoming-banner>
            <div class="font-bold text-carbon-80">{{status.title}}</div>
            {{#if status.newDeskLine}}
              <div class="text-carbon-60">{{status.newDeskLine}}</div>
            {{/if}}
            {{#if status.dateLine}}
              <div class="text-carbon-60">{{status.dateLine}}</div>
            {{/if}}
          </div>
        {{/each}}

        {{! Pending request banners }}
        {{#each this.pendingStatuses as |status|}}
          <div class="mb-4 rounded-lg bg-gem-5 p-3" data-test-profile-card-pending-banner>
            <div class="font-bold text-carbon-80">{{status.title}}</div>
            {{#if status.newDeskLine}}
              <div class="text-carbon-60">{{status.newDeskLine}}</div>
            {{/if}}
            <button class="mt-1 text-gem-50" type="button" data-test-profile-card-cancel-request>
              Cancel request
            </button>
          </div>
        {{/each}}

        {{! Assigned parking section }}
        {{#if this.parkingText}}
          <div class="mb-4">
            <div class="mb-1 font-bold text-carbon-50" data-test-profile-card-parking-label>Assigned parking</div>
            <div class="text-carbon-70" data-test-profile-card-parking-info>
              {{this.parkingText}}
            </div>
          </div>
        {{/if}}

        {{! Separator }}
        <hr class="mb-4 mt-1 border-carbon-10" />

        {{! Action button }}
        <button
          class="w-full rounded-lg border border-carbon-20 px-4 py-2 text-sm text-carbon-70
            {{unless this.canRequest 'cursor-not-allowed opacity-50'}}"
          type="button"
          disabled={{not this.canRequest}}
          data-test-profile-card-request-button
        >
          {{this.buttonText}}
        </button>
      {{/if}}
    </div>
  </template>
}
